import { apiClient } from './api-client';
import { API_CONFIG } from './config';
import { LoginRequest, LoginResponse, AuthUser } from '@/types';

/**
 * Authentication service for handling login and user session management
 */
export class AuthService {
  /**
   * Login user with email and password
   */
  public async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>(
        API_CONFIG.ENDPOINTS.LOGIN,
        credentials
      );

      // Store authentication token
      if (response.token) {
        apiClient.setAuthToken(response.token);
      }

      return response;
    } catch (error) {
      // Clear any existing auth data on login failure
      apiClient.clearAuthData();
      throw error;
    }
  }

  /**
   * Logout user and clear authentication data
   */
  public logout(): void {
    // Simply clear local authentication data
    apiClient.clearAuthData();
  }

  /**
   * Get current authenticated user
   * Note: User data is not stored in localStorage, only the token
   */
  public getCurrentUser(): AuthUser | null {
    // User data is not persisted, only available during the session
    // If you need user data, fetch it from the backend using the stored token
    return null;
  }

  /**
   * Check if user is currently authenticated
   */
  public isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }



  /**
   * Get authentication headers for manual requests
   */
  public getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = apiClient.isAuthenticated() ? localStorage.getItem(API_CONFIG.STORAGE_KEYS.TOKEN) : null;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Always use default tenant ID from config
    headers['X-Tenant-Id'] = API_CONFIG.DEFAULT_TENANT_ID;

    return headers;
  }

  /**
   * Validate if current session is still valid
   * This could be enhanced to check token expiration
   */
  public isSessionValid(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    // Add additional validation logic here if needed
    // For example, check token expiration time
    
    return true;
  }
}

// Export a singleton instance
export const authService = new AuthService();
export default authService;
